import { Component, Input, OnInit, SimpleChanges, OnChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Party } from '../models/party';
import { PartyType } from '../models/party-type';
import { TranslateService } from '@ngx-translate/core';
import { OnyxEvent } from '../models/event';
import { Contact } from '../models/contact';
import { PropertyLookupStore } from '../../shared/lookup/property-lookup/property-lookup.store';
import { LogService } from '../../shared/log/log.service';
import { PropertyLookupDetail } from '../../shared/models/property-lookup-detail';
import { PropertyLookupConverter } from '../utils/property-lookup-converter';
import { AuthenticationStore } from '../../permission/services/authentication.store';
import { User } from '../../auth/models/User';
import { PropertyPopupComponent } from '../property-popup/property-popup.component';
import { Property } from '../models/property';
import { PropertyLookupDetailQuery } from '../../shared/models/property-lookup-detail-query';
import { PioneerValidators } from 'app/shared/utils/pioneer.validators';
import { EventValidationService } from '../service/event-validation.service';
import { Observable } from 'rxjs/Observable';

@Component({
  selector: 'pioneer-property-lookup-form',
  templateUrl: 'property-lookup-form.component.html',
  styleUrls: [ './property-lookup-form.component.css']
})
export class PropertyLookupFormComponent implements OnInit, OnChanges {

  public partyType = PartyType.PROPERTY;

  @Input('parentForm')
  public parentForm: FormGroup;

  public contactLookupForm: FormGroup;
  public onyxEvent: OnyxEvent;

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  @Input()
  public isReadOnly = false;

  @Input()
  public isNewEvent = false;

  @Input()
  public inputEmail: string;

  @Output()
  public outputEmail: EventEmitter<string> = new EventEmitter<string>();

  public contactTypeTitle: string;
  public contactReferenceNumberTitle: string;
  public summaryResults;

  private checkError: boolean;
  private contactFormFilled: boolean;
  private lookupSelected: boolean;

  private user: User;

  public isPropertySelected = false;
  isPropertyEditable$: Observable<boolean>;
  public isContactEditable = true;

  public showSubscriberBadge = false;

  get propertyStore(): PropertyLookupStore {
    return this.propertyLookupStore;
  }

  showAdvancedPropertySearch: boolean;

  @ViewChild(PropertyPopupComponent)
  public propertyPopup: PropertyPopupComponent;

  constructor(private formBuilder: FormBuilder,
              private translateService: TranslateService,
              private propertyLookupStore: PropertyLookupStore,
              private logService: LogService,
              private authenticationStore: AuthenticationStore,
              private eventValidationService: EventValidationService) {
  }

  ngOnInit() {
    this.checkError = false;
    this.contactFormFilled = false;

    if (!this.onyxEvent) {
      this.onyxEvent = new OnyxEvent();
    }

    this.authenticationStore.user.subscribe((user: User) => {
      this.user = user;
    });

    this.translateService.get('EVENT.CONTACT_PROPERTY').subscribe(text => this.contactTypeTitle = text);
    this.translateService.get('EVENT.CONTACT_PROPERTY_REF_CODE')
      .subscribe(text => this.contactReferenceNumberTitle = text);

    this.contactLookupForm = this.dataToFormGroup(this.onyxEvent.eventProperty);
    this.parentForm.setControl('eventProperty', this.contactLookupForm);

    // Load the user prop info in case of single hotel user
    if (this.isSingleProperty() && this.isNewEvent) {
      const propertyQuery = new PropertyLookupDetailQuery({
        chain: this.user.allChains[0],
        brand: this.user.allBrands[0],
        propertyId: this.user.allProperties[0],
      });
      this.onSelectedProperty(propertyQuery);
    }

    /*
     Wire up Property Name Search
     */
    this.propertyLookupStore.selected.subscribe((details: PropertyLookupDetail) => {
      this.logService.verbose(`property details ${JSON.stringify(details)}`);

      PropertyLookupConverter.updateFormFields(details, this.contactLookupForm, this.propertyLookupStore);
    });

    if (this.onyxEvent.eventProperty.eventPartyId) {
      this.isPropertySelected = true;
    }

    this.isContactEditable = this.authenticationStore.hasPermission('EditPropertyContact');

    this.showAdvancedPropertySearch = this.authenticationStore.hasPermission('ViewExtendedPropertySearch');

    this.isPropertyEditable$ = this.eventValidationService.canChangeProperty();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes[ 'event' ]) {
      return;
    }

    this.dataToFormGroup(this.onyxEvent.eventProperty);
  }

  inlineSearchTouched(): void {
    // Check for an error if the user has never tried to select a property
    // A delay is required so that this logic is done after onSelectedProperty()
    setTimeout(() => {
      this.checkError = !this.contactFormFilled;

      if (!this.lookupSelected) {
        this.propertyLookupStore.clearSearchResults();
      } else {
        this.lookupSelected = false;
      }
    }, 100);
  }

  isPropertyMissing(): boolean {
    return this.checkError && this.contactLookupForm.hasError('propertyRequired');
  }

  isReadyOnlyProperty(): boolean {
    return this.isSingleProperty();
  }

  updateResults(results): void {
    this.logService.verbose(`results: ${results}`);
    this.summaryResults = results;
  }

  onSelectedProperty(propertyQuery: PropertyLookupDetailQuery) {
    this.contactFormFilled = true;
    this.lookupSelected = true;
    this.isPropertySelected = true;
    this.logService.debug(`propertySummary ${JSON.stringify(propertyQuery)}`);
    this.propertyLookupStore.getDetail(propertyQuery).subscribe();
  }

  private dataToFormGroup(party: Party): FormGroup {

    if (!party) {
      party = new Party();
    } else {
      if (!party.contact) {
        party.contact = new Contact();
      }
      party = new Party(party);
    }

    if (!this.isNewEvent) {
      // onyxEvent chain/brand/propertyId is added to party if the event already existed
      party = party.merge({
        propertyId: party.propertyId,
        chain: party.chain,
        brand: party.brand
      }) as Party;
    }

    let refCodeValidator = [party.eventReferenceNumber, Validators.nullValidator];

    if (!this.contactLookupForm) {
      const emailValidator = !this.isReadOnly && this.authenticationStore.hasPermission('EditPropertyContact') ?
      [Validators.required, Validators.maxLength(75), PioneerValidators.isValidEmail] : Validators.nullValidator;
      this.contactLookupForm = this.formBuilder.group({
        propertyNameSearch: '',
        agencyNameSearch: '',
        name: party.name,
        line1: party.line1,
        line2: party.line2,
        partyType: PartyType.PROPERTY,
        city: party.city,
        state: party.state,
        postalCode: party.postalCode,
        agencyId: party.agencyId,
        code: party.code,
        propertyId: party.propertyId,
        chain: party.chain,
        brand: party.brand,
        eventReferenceNumber: refCodeValidator,
        manuallyAdded: party.manuallyAdded,
        autoGeneratedCode: party.autoGeneratedCode,
        countryCode: party.countryCode,
        contact: this.formBuilder.group({
          firstName: [ party.contact.firstName ],
          lastName: [ party.contact.lastName ],
          partyType: PartyType.PROPERTY,
          email: [ party.contact.email, emailValidator ],
          phone: [ party.contact.phone ],
          fax: [ party.contact.fax ]
        })
      }, {
        validator: PioneerValidators.isPropertyError
      });
    } else {
      this.contactLookupForm.patchValue({
        propertyNameSearch: '',
        agencyNameSearch: '',
        name: party.name,
        line1: party.line1,
        line2: party.line2,
        partyType: PartyType.PROPERTY,
        city: party.city,
        state: party.state,
        postalCode: party.postalCode,
        agencyId: party.agencyId,
        code: party.code,
        propertyId: party.propertyId,
        chain: party.chain,
        brand: party.brand,
        eventReferenceNumber: party.eventReferenceNumber,
        manuallyAdded: party.manuallyAdded,
        autoGeneratedCode: party.autoGeneratedCode,
        countryCode: party.countryCode
      });
      this.contactLookupForm.controls[ 'contact' ].patchValue({
        firstName: party.contact.firstName,
        lastName: party.contact.lastName,
        partyType: PartyType.PROPERTY,
        email: party.contact.email,
        phone: party.contact.phone,
        fax: party.contact.fax
      });

      if (party.contact.email) {
        this.outputEmail.emit(party.contact.email);
      }

    }

    this.showSubscriberBadge = party.subscriber && this.authenticationStore.hasPermission('ViewGroupPayPropertyIndicators');

    return this.contactLookupForm;
  }

  public isSingleProperty(): boolean {
    return this.authenticationStore.isSinglePropertyUser();
  }

  public isPropertyInlineSearchEnabled(): boolean {
    return !this.isSingleProperty() && !this.isReadOnly;
  }

  public get showLongLabels(): boolean {
    return !this.onyxEvent.eventProperty.subscriber || (this.isNewEvent && this.authenticationStore.hasPermission('EditPropertyContact'));
  }

  openAddPropertyPopup(event: any) {
    event.preventDefault();
    this.propertyPopup.open();
  }

  closedPropertyPopup(property: Property) {
    if (property) {
      const eventProperty = PropertyLookupConverter.propertyToParty(property, true);
      this.logService.info(JSON.stringify(eventProperty));
      this.contactLookupForm = this.dataToFormGroup(eventProperty);
      this.parentForm.setControl('eventProperty', this.contactLookupForm);
      this.contactFormFilled = true;
      this.isPropertySelected = true;
    }
  }

  openAdvancedPropertySearchPopup() {
    this.propertyPopup.open();
  }

  setupPropertyEmail(email: string): void {
    this.outputEmail.emit(email);
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/property-lookup-form/property-lookup-form.component.ts